import * as Sentry from "@sentry/react";

import { headers, serialize } from "../helpers/api";
import { HOST } from "../constants/api";
import history from "../helpers/history";

export function get(url = "", params = {}, handler = true) {
  return fetch(HOST + url + serialize(params), {
    headers: headers(),
    credentials: "omit",
  })
    .then((res) => (handler ? _handleResponse(res) : res))
    .catch((e) => _handleRequestFailure(e, "get", url));
}

export function post(url = "", data = {}, handler = true) {
  return fetch(HOST + url, {
    method: "post",
    headers: headers(),
    credentials: "omit",
    body: JSON.stringify(data),
  })
    .then((res) => (handler ? _handleResponse(res) : res))
    .catch((e) => _handleRequestFailure(e, "post", url));
}
export function postExternal(url = "", data = {}, handler = true) {
  fetch(url, {
    method: "post",
    body: JSON.stringify(data),
  }).catch((e) => _handleRequestFailure(e, "postExternal", url));
}
export function put(url = "", data = {}) {
  return fetch(HOST + url, {
    method: "put",
    headers: headers(),
    credentials: "omit",
    body: JSON.stringify(data),
  })
    .then((res) => _handleResponse(res))
    .catch((e) => _handleRequestFailure(e, "put", url));
}

export function destroy(url = "", data = {}) {
  return fetch(HOST + url, {
    method: "delete",
    headers: headers(),
    body: JSON.stringify(data),
    credentials: "omit",
  }).catch((e) => _handleRequestFailure(e, "destroy", url));
}

export async function upload(formData) {
  const bearerToken = window.localStorage.getItem("bearerToken");

  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();

    xhr.open("POST", `${HOST}/files`, true);
    xhr.setRequestHeader("Authorization", `Bearer ${bearerToken}`);
    xhr.onload = () => resolve(xhr.response);
    xhr.onerror = () => undefined;
    xhr.send(formData);
  });
}
export async function uploadQR(formData, returncall) {
  const bearerToken = window.localStorage.getItem("bearerToken");
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${HOST}/files/qr`, true);
    xhr.setRequestHeader("Authorization", `Bearer ${bearerToken}`);
    xhr.setRequestHeader("Accept", "application/json");
    //xhr.setRequestHeader("Content-Type",'application/json');
    xhr.onload = () => returncall(JSON.parse(xhr.response));
    xhr.onerror = () => undefined;
    xhr.send(formData);
  });
}
/**
 * The default handling for a response: JSON on success, otherwise the
 * error page / logout flow. Exported for callers that fetch with
 * `handler = false` to inspect a specific error first and then want the
 * usual treatment for everything else.
 */
export const handleResponse = (res) => _handleResponse(res);

async function _handleResponse(res) {
  if (res.ok) {
    switch (res.status) {
      case 204:
        return res;

      default:
        return await res.json();
    }
  } else {
    try {
      var error = "";
      if (res.status === 401) {
        const body = await res.json();

        if (body.responseStatus.message === "Invalid Username or Password") {
          return body;
        }
      }
      if (res.status == 404) {
        // no access
      }
      if (res.status === 500) {
        //socket erroir
      }

      const id = new Date().valueOf();
      error = "Sorry. Something went wrong. Try again.";
      try {
        var log_error = await res.json();
        log_error.responseStatus.url = window.location.href;
        const trace = JSON.stringify(log_error, null, 2);
        window.localStorage.setItem(`error-${id}`, trace);
        if (process.env.REACT_APP_ENV === "PRODUCTION") {
          sendCrashLogs(log_error, trace);
        }
      } catch (E) {}

      if (res.status === 401) {
        //Insufficent permissions
        _forceLogOut(true); //silent
        return;
      }

      return history.push(`/error/${res.status}/${id}`);
    } catch (e) {
      /** Last-ditch effort, log out */
      _forceLogOut(false);
    }
  }
}
/**
 * Network-level failures (offline, dropped wifi, sleeping laptop, a navigation
 * that aborts an in-flight request) reject the fetch promise. Call sites
 * overwhelmingly `await` without a try/catch, so those rejections escaped as
 * unhandled rejections and were reported as crashes.
 *
 * Resolve to `undefined` instead. That is already the failure value
 * `_handleResponse` returns for error responses, so callers that cope with a
 * failed request today cope with a dropped connection too. The failure is still
 * recorded, but as handled, so a genuine API outage remains visible.
 */
function _handleRequestFailure(error, method, url) {
  /**
   * A rejected fetch means the request never reached the server, so the browser
   * being offline produces exactly the same `TypeError: Failed to fetch` as a
   * genuine outage. Sentinel re-polls every 30 seconds, so one dropped wifi
   * session files a steady stream of identical events that nothing can act on.
   *
   * `navigator.onLine` is only trustworthy as a negative — false really does
   * mean no connection, while true says nothing about reachability. Gating on
   * false therefore drops the unactionable half and still reports an outage
   * where the browser believes it is online. The breadcrumb keeps the dropped
   * requests visible on whatever event is reported next.
   */
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    Sentry.addBreadcrumb({
      category: "api",
      level: "warning",
      message: `${method} ${url} failed while offline`,
    });

    return undefined;
  }

  Sentry.captureException(error, {
    level: "warning",
    tags: { api_method: method, api_url: url },
    /**
     * Group by the failure itself rather than the call stack. Every caller
     * reaches this through a different React render path, so the default
     * grouping split a single unreachable-API moment across one issue per
     * endpoint. The method and url tags still break the numbers down.
     */
    fingerprint: ["api-request-failed"],
  });

  return undefined;
}
function sendCrashLogs(error, trace) {
  var res = post("/crashlog", {
    error: error,
    stacktrace: trace,
  });
}
function _forceLogOut(silent) {
  window.localStorage.removeItem("refreshToken");
  window.localStorage.removeItem("bearerToken");
  if (silent) {
    window.location.replace("/log-in");
  } else {
    window.location.replace("/log-in?tokenExpired=true");
  }
}
